import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../../api/users';
import { useConfirmation } from '../../../hooks/useConfirmation';

interface UseActionMenuProps {
  onDelete?: () => void;
  itemName?: string;
}

export const useActionMenu = ({ onDelete, itemName = 'item' }: UseActionMenuProps) => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const { userData } = useAuth();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const instanceId = useRef<string>(`actionmenu_${Math.random().toString(36).slice(2)}`);

  const {
    isOpen: isConfirmationOpen,
    config: confirmationConfig,
    handleConfirm,
    openConfirmation,
    closeConfirmation,
  } = useConfirmation(onDelete || (() => {}));

  const calculateMenuPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobile = viewportWidth <= 768;

      let top = rect.bottom + 8;
      let left = rect.right - 120;

      if (isMobile) {
        left = Math.max(8, Math.min(left, viewportWidth - 128));
        if (top + 100 > viewportHeight) {
          top = Math.max(8, rect.top - 100);
        }
        if (top < 8) {
          top = 8;
        }
      } else {
        if (left < 0) {
          left = 8;
        } else if (left + 120 > viewportWidth) {
          left = viewportWidth - 128;
        }

        if (top + 100 > viewportHeight) {
          top = rect.top - 100;
        }
        if (top < 8) {
          top = 8;
        }
      }

      setMenuPosition({ top, left });
    }
  }, []);

  const handleMenuToggle = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      const next = !openMenu;
      if (next) {
        calculateMenuPosition();
        window.dispatchEvent(
          new CustomEvent('actionmenu-open', { detail: { id: instanceId.current } })
        );
      }
      setOpenMenu(next);
    },
    [openMenu, calculateMenuPosition]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      openConfirmation({
        title: `Are you sure you want to delete this ${itemName}?`,
        message: `This action cannot be undone. Once deleted, this ${itemName} will be permanently removed.`,
        variant: 'danger',
      });
      handleMenuToggle(e);
    },
    [openConfirmation, itemName, handleMenuToggle]
  );

  const handleActionClick = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement>,
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
    ) => {
      e.stopPropagation();
      e.preventDefault();
      onClick(e);
      handleMenuToggle(e);
    },
    [handleMenuToggle]
  );

  const closeMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    if (triggerRef.current && !triggerRef.current.contains(target)) {
      setOpenMenu(false);
    }
  }, []);

  useEffect(() => {
    const handleAnotherMenuOpen = (e: Event) => {
      const { id } = (e as CustomEvent<{ id?: string }>).detail || {};
      if (id !== instanceId.current) {
        setOpenMenu(false);
      }
    };

    const handleResize = () => {
      if (openMenu) {
        calculateMenuPosition();
      }
    };

    window.addEventListener('actionmenu-open', handleAnotherMenuOpen as EventListener);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('actionmenu-open', handleAnotherMenuOpen as EventListener);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [openMenu, calculateMenuPosition]);

  const canAccess = userData?.role === UserRole.ADMIN || userData?.role === UserRole.DIRECTOR;

  return {
    openMenu,
    menuPosition,
    triggerRef,
    isConfirmationOpen,
    confirmationConfig,
    handleConfirm,
    closeConfirmation,
    handleMenuToggle,
    handleDelete,
    handleActionClick,
    closeMenu,
    handleClickOutside,
    canAccess,
  };
};
